import { useEffect, useMemo, useRef, useState, useDeferredValue, useTransition } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { KanbanHeader } from "./components/KanbanHeader";
import { KanbanBoard } from "./components/KanbanBoard";
import { FormViewType } from "../Tickets/Tickets";
import React from "react";
import "./kanban-tailwind.css";
import useStatuses from "../../../store/ReactQueries/statuses/useStatus";
import useUsers from "../../../store/ReactQueries/users/useUser";
import { UserInterface } from "../../../models/UserModel/User";
import KanbanStatusesPreferenceService from "../../../services/ticketService/kanbanStatusesPreferenceService";
import "react-loading-skeleton/dist/skeleton.css";
import KanbanBoardSkeleton from "./components/KanbanBoardSkeleton";
import KanbanCardsSkeleton from "./components/KanbanCardsSkeleton";
import { useTicketFiltering } from "./hooks/useTicketFiltering";
import useUpdateKanbanStatus from "../../../store/ReactQueries/tickets/useUpdateCanbanStatus";
import useKanbanTickets from "../../../store/ReactQueries/tickets/useKanbanTickets";

export type ViewMode = "week" | "day" | "board";

type MembersUpdater = string[] | ((prev: string[]) => string[]);

interface KanbanBoardPageProps {
  formView: FormViewType;
  setFormView: React.Dispatch<React.SetStateAction<FormViewType>>;
  darkMode: boolean;
}

export const KanbanBoardPage: React.FC<KanbanBoardPageProps> = ({ formView, setFormView, darkMode }) => {
  const prevUserIdsRef = useRef<string[]>([]);

  const startOfWeekMonday = (date: Date) => {
    const d = new Date(date);
    const day = d.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + mondayOffset);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const endOfWeekSunday = (weekStartMonday: Date) => {
    const d = new Date(weekStartMonday);
    d.setDate(d.getDate() + 6);
    d.setHours(23, 59, 59, 999);
    return d;
  };

  const formatYYYYMMDD = (d: Date) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const parseYYYYMMDDToDate = (value: string) => {
    // value is "YYYY-MM-DD"
    const [y, m, d] = value.split("-").map(Number);
    const date = new Date(y, (m ?? 1) - 1, d ?? 1);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const firstDayOfCurrentMonth = () => {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth(), 1);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const todayStart = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const [viewMode, setViewMode] = useState<ViewMode>("board");

  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => startOfWeekMonday(new Date()));
  const [selectedDay, setSelectedDay] = useState<Date>(new Date());

  const [customStartDate, setCustomStartDate] = useState<Date>(() => firstDayOfCurrentMonth());
  const [customEndDate, setCustomEndDate] = useState<Date>(() => todayStart());

  const [teamMembers, setTeamMembers] = useState<UserInterface[]>([]);
  const [statusesSelectionLoaded, setStatusesSelectionLoaded] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const deferredSearchInput = useDeferredValue(searchInput);

  const [showFilterSkeleton, setShowFilterSkeleton] = useState(false);

  const { data: statuses = [], isLoading: statusesLoading } = useStatuses(0, 0);
  const { data: users = [], isLoading: usersLoading } = useUsers(0, 0);

  const updateTicketStatus = useUpdateKanbanStatus();
  const suppressSkeletonRef = useRef(false);

  const sortedStatuses = useMemo(() => {
    return [...(statuses || [])].sort((a: any, b: any) => {
      const ao = typeof a?.order === "number" ? a.order : 0;
      const bo = typeof b?.order === "number" ? b.order : 0;
      return ao - bo;
    });
  }, [statuses]);

  const [showSkeleton, setShowSkeleton] = useState(true);

  const [isMembersPending, startMembersTransition] = useTransition();
  const deferredSelectedMembers = useDeferredValue(selectedMembers);

  const isFilteringRaw = searchInput !== deferredSearchInput;

  const weekStart = useMemo(() => startOfWeekMonday(currentWeekStart), [currentWeekStart]);
  const weekEnd = useMemo(() => endOfWeekSunday(weekStart), [weekStart]);

  const queryStartDate = useMemo(() => {
    return viewMode === "board" ? customStartDate : weekStart;
  }, [viewMode, customStartDate, weekStart]);

  const queryEndDate = useMemo(() => {
    return viewMode === "board" ? customEndDate : weekEnd;
  }, [viewMode, customEndDate, weekEnd]);

  const startDate = useMemo(() => formatYYYYMMDD(queryStartDate), [queryStartDate]);
  const endDate = useMemo(() => formatYYYYMMDD(queryEndDate), [queryEndDate]);

  const {
    data: tickets = [],
    isLoading: ticketsLoading,
    isFetching: ticketsFetching,
  } = useKanbanTickets(0, 0, undefined, startDate, endDate);

  useEffect(() => {
    const shouldShow = isFilteringRaw || (ticketsFetching && !suppressSkeletonRef.current);

    if (shouldShow) {
      setShowFilterSkeleton(true);
      return;
    }

    const t = window.setTimeout(() => setShowFilterSkeleton(false), 300);
    return () => window.clearTimeout(t);
  }, [isFilteringRaw, ticketsFetching]);

  useEffect(() => {
    if (!sortedStatuses || sortedStatuses.length === 0) return;
    if (statusesSelectionLoaded) return;

    const load = async () => {
      try {
        const saved = await KanbanStatusesPreferenceService.getSelectedStatuses();

        const allStatusIds = sortedStatuses.map((s) => s.id).filter(Boolean) as string[];
        setSelectedStatuses(saved?.length ? saved : allStatusIds);
      } catch (err) {
        console.error("Failed to load saved statuses, using all as default", err);
        const allStatusIds = sortedStatuses.map((s) => s.id).filter(Boolean) as string[];
        setSelectedStatuses(allStatusIds);
      } finally {
        setStatusesSelectionLoaded(true);
      }
    };

    load();
  }, [sortedStatuses, statusesSelectionLoaded]);

  useEffect(() => {
    if (!users?.length) return;

    const nextMembers = users as UserInterface[];
    const nextIds = nextMembers.map((u) => u.id).filter(Boolean) as string[];

    setTeamMembers(nextMembers);

    setSelectedMembers((prevSelected) => {
      if (!prevSelected?.length) return nextIds;

      const nextIdSet = new Set(nextIds);
      const kept = prevSelected.filter((id) => nextIdSet.has(id));

      const prevIds = prevUserIdsRef.current;
      const prevHadAllSelected = prevIds.length > 0 && prevSelected.length === prevIds.length;

      if (!prevHadAllSelected) return kept;

      const newOnes = nextIds.filter((id) => !prevIds.includes(id));
      return [...kept, ...newOnes];
    });

    prevUserIdsRef.current = nextIds;
  }, [users]);

  useEffect(() => {
    const anyLoading = statusesLoading || usersLoading || ticketsLoading;

    if (anyLoading) setShowSkeleton(true);

    const t = setTimeout(() => {
      if (!anyLoading) setShowSkeleton(false);
    }, 400);

    return () => clearTimeout(t);
  }, [statusesLoading, ticketsLoading, usersLoading]);

  const handleCustomStartChange = (value: string) => {
    const next = parseYYYYMMDDToDate(value);
    setCustomStartDate(next);

    if (next.getTime() > customEndDate.getTime()) {
      setCustomEndDate(next);
    }
  };

  const handleCustomEndChange = (value: string) => {
    const next = parseYYYYMMDDToDate(value);
    setCustomEndDate(next);

    // if end < start, move start to end
    if (next.getTime() < customStartDate.getTime()) {
      setCustomStartDate(next);
    }
  };

  const filtering = useTicketFiltering(tickets, sortedStatuses, {
    searchText: deferredSearchInput,
    selectedMembers: deferredSelectedMembers,
    selectedStatuses,
  });

  const handleTicketMove = (ticketId: string, newStatusId: string) => {
    const newStatus = sortedStatuses.find((s) => s.id === newStatusId);

    suppressSkeletonRef.current = true;

    updateTicketStatus.mutate(
      {
        ticketId,
        newStatusId,
        newStatus,
      },
      {
        onSettled: () => {
          window.setTimeout(() => {
            suppressSkeletonRef.current = false;
          }, 350);
        },
      }
    );
  };

  const handleWeekChange = (newWeekStart: Date) => {
    const monday = startOfWeekMonday(newWeekStart);
    setCurrentWeekStart(monday);

    if (viewMode === "day") {
      const weekEndLocal = new Date(monday);
      weekEndLocal.setDate(monday.getDate() + 6);

      if (selectedDay < monday || selectedDay > weekEndLocal) {
        setSelectedDay(monday);
      }
    }
  };

  const handleSelectedStatusesChange = (newSelected: string[]) => {
    setSelectedStatuses(newSelected);

    KanbanStatusesPreferenceService.saveSelectedStatuses(newSelected).catch((err) => {
      console.error("Failed to save selected statuses", err);
    });
  };

  const handleSearchChange = (q: string) => {
    setSearchInput(q);
  };

  const handleSelectedMembersChange = (updater: MembersUpdater) => {
    startMembersTransition(() => {
      setSelectedMembers((prev) => (typeof updater === "function" ? updater(prev) : updater));
    });
  };

  if (showSkeleton) {
    return <KanbanBoardSkeleton />;
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className={`flex ${darkMode ? "kanban-dark" : ""}`}>
        <div className="flex-1 flex flex-col overflow-hidden" style={{ height: "88vh" }}>
          <KanbanHeader
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            teamMembers={teamMembers}
            statuses={sortedStatuses}
            selectedStatuses={selectedStatuses}
            onSelectedStatusesChange={handleSelectedStatusesChange}
            currentWeekStart={currentWeekStart}
            onWeekChange={handleWeekChange}
            selectedDay={selectedDay}
            onDayChange={setSelectedDay}
            formView={formView}
            setFormView={setFormView}
            searchQuery={searchInput}
            onSearchChange={handleSearchChange}
            selectedMembers={selectedMembers}
            onSelectedMembersChange={handleSelectedMembersChange}
            darkMode={darkMode}
            customStartDate={formatYYYYMMDD(customStartDate)}
            customEndDate={formatYYYYMMDD(customEndDate)}
            onCustomStartDateChange={handleCustomStartChange}
            onCustomEndDateChange={handleCustomEndChange}
          />

          {showFilterSkeleton ? (
            <KanbanCardsSkeleton columns={selectedStatuses?.length || 5} cardsPerColumn={3} />
          ) : (
            <KanbanBoard
              tickets={filtering.filteredTickets}
              statuses={filtering.visibleStatuses}
              viewMode={viewMode}
              currentWeekStart={currentWeekStart}
              onTicketMove={handleTicketMove}
              selectedDay={selectedDay}
              onDayChange={setSelectedDay}
              selectedStatuses={selectedStatuses}
              darkMode={darkMode}
              ticketsByStatus={filtering.byStatus}
              rangeStart={queryStartDate}
              rangeEnd={queryEndDate}
            />
          )}
        </div>
      </div>
    </DndProvider>
  );
};

export default KanbanBoardPage;
